import { useMemo, useState } from 'react';
import {
  EnsaioDownloadLink,
  useEnsaioMaterial,
} from '@/modules/simulado/presentation/components/EnsaioMaterials';
import { breakdownByArea } from '@/modules/diagnostico/domain/entities/area-breakdown';
import { AreaBreakdown } from '@/modules/diagnostico/presentation/components/AreaBreakdown';
import { track } from '@/lib/analytics';
import { useTenant } from '@/modules/tenant/presentation/hooks/use-tenant';
import { AuthModal } from '@/modules/auth/presentation/components/AuthModal';
import { useMySubmission } from '@/modules/submissions/presentation/hooks/use-submissions';
import { useExam } from '@/modules/simulado/presentation/hooks/use-exam';
import { useSimuladoDraft } from '@/modules/simulado/presentation/hooks/use-simulado-draft';
import { useFinishEnsaio } from '@/modules/simulado/presentation/hooks/use-finish-ensaio';
import { EnsaioRunner } from '@/modules/simulado/presentation/components/EnsaioRunner';
import {
  fromAnswersByQuestionId,
  toAnswerKey,
  toSimuladoQuestions,
} from '@/modules/simulado/domain/entities/question.entity';
import { diagnose } from '@/modules/diagnostico/domain/entities/diagnose';
import { DiagnosticoReading } from '@/modules/diagnostico/presentation/components/DiagnosticoReading';
import { EmptyState, TabSkeleton } from './EmptyState';
import { InviteSection } from './InviteSection';
import { ReviewQuestionCard } from './ReviewQuestionCard';
import { Tabs, tabId } from './Tabs';

export type EnsaioSubTab = 'questoes' | 'diagnostico';

const SUB_TABS: { id: EnsaioSubTab; label: string }[] = [
  { id: 'questoes', label: 'Questões' },
  { id: 'diagnostico', label: 'Diagnóstico' },
];

/**
 * A aba Ensaio.
 *
 * - **Quem já enviou** vê o que fez, em duas sub-abas: as questões corrigidas
 *   e o diagnóstico.
 * - **Quem não enviou** — chegou pelo gabarito, dias depois, sem ter feito o
 *   ensaio — pode fazê-lo aqui mesmo, sem sair do painel. Ao finalizar, o
 *   envio é direto (já há sessão) e a aba vira o resultado.
 */
export function EnsaioTab({
  sub,
  onSubChange,
}: {
  sub: EnsaioSubTab;
  onSubChange: (sub: EnsaioSubTab) => void;
}) {
  const myEnsaio = useMySubmission('ensaio');

  if (myEnsaio.isPending) return <TabSkeleton label="Carregando seu ensaio" />;
  if (myEnsaio.isError) {
    return (
      <EmptyState
        title="Ensaio"
        note="Não conseguimos carregar seu ensaio agora. Atualize a página em alguns instantes."
      />
    );
  }

  if (!myEnsaio.data) {
    return <EnsaioToDo onDone={() => onSubChange('diagnostico')} />;
  }

  return (
    <EnsaioResult
      answers={myEnsaio.data.answers}
      sub={sub}
      onSubChange={onSubChange}
    />
  );
}

function EnsaioToDo({ onDone }: { onDone: () => void }) {
  const { edition } = useTenant();
  const { draft } = useSimuladoDraft();
  const [isRunning, setIsRunning] = useState(false);
  const { finish, authModalProps, isSubmitting, isError } =
    useFinishEnsaio(onDone);
  const started = Object.keys(draft.answers).length > 0;

  if (!isRunning) {
    return (
      <EmptyState
        title="Você ainda não fez o ensaio"
        note={`São ${edition.ensaioQuestionIds.length} questões, e ao final você recebe o seu diagnóstico. ${
          started ? 'Suas respostas de antes continuam salvas.' : ''
        }`}
      >
        <button
          type="button"
          className="cta cta-inline"
          onClick={() => {
            track({
              event: 'cta_clicado',
              cta: 'comecar_ensaio',
              local: 'painel',
            });
            setIsRunning(true);
          }}
        >
          {started ? 'Continuar o ensaio' : 'Fazer o ensaio agora'}
        </button>
      </EmptyState>
    );
  }

  return (
    <>
      <EnsaioRunner
        onFinish={finish}
        isSubmitting={isSubmitting}
        submitError={isError}
      />
      <AuthModal {...authModalProps} />
    </>
  );
}

function EnsaioResult({
  answers: payload,
  sub,
  onSubChange,
}: {
  answers: Record<string, number>;
  sub: EnsaioSubTab;
  onSubChange: (sub: EnsaioSubTab) => void;
}) {
  const { exam } = useTenant();
  const hasEnsaioMaterial = useEnsaioMaterial() !== null;
  const examQuery = useExam();

  const view = useMemo(() => {
    if (!examQuery.data) return null;
    const answers = fromAnswersByQuestionId(
      toSimuladoQuestions(examQuery.data),
      payload,
    );
    const answerKey = toAnswerKey(examQuery.data);
    return {
      answers,
      reading: diagnose(answerKey, answers),
      areas: breakdownByArea(examQuery.data, answerKey, answers),
    };
  }, [examQuery.data, payload]);

  return (
    <div className="stack-lg">
      <Tabs
        idPrefix="ensaio"
        label="Ensaio"
        options={SUB_TABS}
        active={sub}
        onChange={onSubChange}
        size="sm"
      />

      {/* "Baixar o ensaio", que continua útil depois do ensaio feito: logo
          abaixo das sub-abas, à direita. As Previsões ficam acima das abas
          principais do painel (`PainelPage`). */}
      {hasEnsaioMaterial && (
        <div className="ensaio-materials">
          <EnsaioDownloadLink />
        </div>
      )}

      <div role="tabpanel" aria-labelledby={tabId('ensaio', sub)}>
        {examQuery.isPending && <TabSkeleton label="Carregando o ensaio" />}
        {examQuery.isError && (
          <EmptyState
            title="Ensaio"
            note="Não conseguimos carregar as questões agora. Atualize a página em alguns instantes."
          />
        )}

        {view && sub === 'questoes' && examQuery.data && (
          <div className="stack-md">
            {examQuery.data.map((question) => (
              <ReviewQuestionCard
                key={question.questionId}
                question={question}
                picked={view.answers[question.number]}
              />
            ))}
          </div>
        )}

        {view && sub === 'diagnostico' && (
          <div className="stack-lg">
            {view.reading ? (
              <DiagnosticoReading
                reading={view.reading}
                examName={exam.shortName}
                subject="ensaio"
              />
            ) : (
              <EmptyState
                title="Diagnóstico"
                note="As questões deste ensaio ainda não têm a dificuldade classificada. Seu diagnóstico aparece aqui assim que forem analisadas."
              />
            )}
            <AreaBreakdown areas={view.areas} />
            {/* Logo depois do resultado: é quando a pessoa tem o que contar. */}
            <InviteSection />
          </div>
        )}
      </div>
    </div>
  );
}
